/**
 * ownCloud (oCIS) WebDAV access for the "Unterschriften-Mappe": list the PDFs
 * in the input folder (with their optional summary .txt), download one, and
 * -- after signing -- store the signed PDF in the output folder and remove the
 * originals.
 */
import { AuthRequired, getAccessToken, type MappeConfig } from "./oidc";

export const SUMMARY_MAX_CHARS = 200;

const PROPFIND_BODY =
  '<?xml version="1.0"?><d:propfind xmlns:d="DAV:"><d:prop><d:resourcetype/></d:prop></d:propfind>';

// oCIS serves the user's personal space at this legacy path.
function davUrl(cfg: MappeConfig, folder: string, name?: string): string {
  const base = `${cfg.ocisUrl}/remote.php/webdav${folder}/`;
  return name === undefined ? base : base + encodeURIComponent(name);
}

async function dav(cfg: MappeConfig, url: string, init: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken(cfg);
  const resp = await fetch(url, {
    ...init,
    headers: { ...init.headers, Authorization: `Bearer ${token}` },
  });
  // The token was rejected (e.g. revoked, or the IdP session ended).
  if (resp.status === 401) throw new AuthRequired();
  return resp;
}

interface Entry {
  name: string;
  collection: boolean;
}

/** Entries directly inside `folder`, or null if the folder doesn't exist. */
async function listEntries(cfg: MappeConfig, folder: string): Promise<Entry[] | null> {
  const resp = await dav(cfg, davUrl(cfg, folder), {
    method: "PROPFIND",
    headers: { Depth: "1", "Content-Type": "application/xml" },
    body: PROPFIND_BODY,
  });
  if (resp.status === 404) return null;
  if (!resp.ok) throw new Error(`Ordner ${folder} konnte nicht gelesen werden (HTTP ${resp.status}).`);

  const xml = new DOMParser().parseFromString(await resp.text(), "application/xml");
  const entries: Entry[] = [];
  const responses = Array.from(xml.getElementsByTagNameNS("DAV:", "response"));
  // The first response is the folder itself.
  for (const entry of responses.slice(1)) {
    const href = entry.getElementsByTagNameNS("DAV:", "href")[0]?.textContent ?? "";
    entries.push({
      name: decodeURIComponent(href.split("/").filter(Boolean).pop() ?? ""),
      collection: entry.getElementsByTagNameNS("DAV:", "collection").length > 0,
    });
  }
  return entries;
}

const isPdf = (name: string) => name.toLowerCase().endsWith(".pdf");

export interface Listing {
  /** PDF file names, sorted naturally. */
  pdfs: string[];
  /** PDF name -> name of its summary text file: the full PDF name plus ".txt". */
  summaries: Map<string, string>;
  /** PDF name -> name of its webhook config file: the full PDF name plus ".json". */
  notifyConfigs: Map<string, string>;
}

// Finds, for each PDF, the sidecar file named "<pdf name>.<ext>" (matched
// case-insensitively on the listing, rather than by probing for the exact name).
function sidecarsByPdf(pdfs: string[], files: string[], ext: string): Map<string, string> {
  const byLowerName = new Map(
    files.filter((f) => f.toLowerCase().endsWith(ext)).map((f) => [f.toLowerCase(), f]),
  );
  const result = new Map<string, string>();
  for (const pdf of pdfs) {
    const sidecar = byLowerName.get(`${pdf.toLowerCase()}${ext}`);
    if (sidecar) result.set(pdf, sidecar);
  }
  return result;
}

export async function listInput(cfg: MappeConfig): Promise<Listing> {
  const entries = await listEntries(cfg, cfg.folder);
  if (entries === null) throw new Error(`Ordner ${cfg.folder} wurde in ownCloud nicht gefunden.`);

  const files = entries.filter((e) => !e.collection).map((e) => e.name);
  const pdfs = files
    .filter(isPdf)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
  return {
    pdfs,
    summaries: sidecarsByPdf(pdfs, files, ".txt"),
    notifyConfigs: sidecarsByPdf(pdfs, files, ".json"),
  };
}

export async function fetchPdf(cfg: MappeConfig, name: string): Promise<File> {
  const resp = await dav(cfg, davUrl(cfg, cfg.folder, name));
  if (!resp.ok) throw new Error(`${name} konnte nicht geladen werden (HTTP ${resp.status}).`);
  return new File([await resp.blob()], name, { type: "application/pdf" });
}

/**
 * The summary text, cut to SUMMARY_MAX_CHARS characters; null if the file is
 * empty. Throws if it can't be read (the caller decides how to show that).
 */
export async function fetchSummary(cfg: MappeConfig, txtName: string): Promise<string | null> {
  const resp = await dav(cfg, davUrl(cfg, cfg.folder, txtName));
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const text = (await resp.text()).replace(/^\uFEFF/, "").trim();
  return text ? Array.from(text).slice(0, SUMMARY_MAX_CHARS).join("") : null;
}

export interface NotifyConfig {
  itemId: string;
  url: string;
}

/** The completion-webhook config for a document: its "item_id" and "url". */
export async function fetchNotifyConfig(cfg: MappeConfig, jsonName: string): Promise<NotifyConfig> {
  const resp = await dav(cfg, davUrl(cfg, cfg.folder, jsonName));
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const data = JSON.parse(await resp.text()) as { item_id?: unknown; url?: unknown };
  if (typeof data.item_id !== "string" || typeof data.url !== "string") {
    throw new Error(`${jsonName} ist ung\u00FCltig (item_id/url fehlen).`);
  }
  return { itemId: data.item_id, url: data.url };
}

/** "name.pdf" -> "name (2).pdf" -> "name (3).pdf" ... until it isn't taken. */
function freeName(desired: string, taken: Set<string>): string {
  if (!taken.has(desired.toLowerCase())) return desired;
  const dot = desired.lastIndexOf(".");
  const stem = dot > 0 ? desired.slice(0, dot) : desired;
  const ext = dot > 0 ? desired.slice(dot) : "";
  for (let n = 2; ; n++) {
    const candidate = `${stem} (${n})${ext}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

async function ensureFolder(cfg: MappeConfig, folder: string): Promise<Set<string>> {
  let entries = await listEntries(cfg, folder);
  if (entries === null) {
    const resp = await dav(cfg, davUrl(cfg, folder), { method: "MKCOL" });
    // 405 = it exists after all (created concurrently).
    if (!resp.ok && resp.status !== 405) {
      throw new Error(`Ordner ${folder} konnte nicht angelegt werden (HTTP ${resp.status}).`);
    }
    entries = (await listEntries(cfg, folder)) ?? [];
  }
  return new Set(entries.map((e) => e.name.toLowerCase()));
}

/**
 * Stores the signed PDF in the output folder (created if missing) under
 * `desiredName`, or a free variant of it -- a WebDAV PUT would silently
 * overwrite an existing file. Returns the name it was stored under.
 */
export async function storeSigned(cfg: MappeConfig, desiredName: string, pdf: Blob): Promise<string> {
  const taken = await ensureFolder(cfg, cfg.outFolder);
  const name = freeName(desiredName, taken);
  const resp = await dav(cfg, davUrl(cfg, cfg.outFolder, name), {
    method: "PUT",
    headers: { "Content-Type": "application/pdf" },
    body: pdf,
  });
  if (!resp.ok) throw new Error(`Signiertes PDF konnte nicht abgelegt werden (HTTP ${resp.status}).`);
  return name;
}

/** Deletes a file from the input folder; already gone (404) counts as done. */
export async function deleteInput(cfg: MappeConfig, name: string): Promise<void> {
  const resp = await dav(cfg, davUrl(cfg, cfg.folder, name), { method: "DELETE" });
  if (!resp.ok && resp.status !== 404) {
    throw new Error(`${name} konnte nicht gelöscht werden (HTTP ${resp.status}).`);
  }
}
